import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAstrologers,
  addAstrologer as addLang,
  updateAstrologer as updateLang,
  deleteAstrologer as deleteLang,
  settleAstrologerPayment,
  getAstrologerPayoutHistory,
  getAstrologerCallHistory,
  getAstrologerChatHistory,
  getChatUsersList,
  getChatMessagesList,
   getCallRecording, 
} from "../../services/astrologerService";
import { toast } from "react-toastify";

const initialState = {
  astrologers: [],
  total: 0,
  page: 1,

  payoutHistory: [],
  payoutStatus: "idle",
  payoutError: null,

  callHistory: [],
  callHistoryTotal: 0,   // ✅ ADD THIS
  callHistoryStatus: "idle",
  callHistoryError: null,

   
  // ✅ NEW — recording state
  callRecording: null,        // { call_id, recordings: [{ signed_url, original }] }
  callRecordingStatus: "idle", // "idle" | "loading" | "succeeded" | "failed"
  callRecordingError: null,

    chatHistory: null, // Changed from array to object since API returns single object
  chatHistoryStatus: "idle",
  chatHistoryError: null,

chatUsersList: [],
chatMessagesList: [],

chatUsersStatus: "idle",
chatMessagesStatus: "idle",

chatUsersError: null,
chatMessagesError: null,

  status: "idle",
  error: null,
};

// Fetch all astrologers (server-side pagination + filters)
export const fetchAstrologers = createAsyncThunk(
  "astrologers/fetchAstrologers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getAstrologers(params);
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch astrologers";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// Fetch astrologer call history
export const fetchCallHistory = createAsyncThunk(
  "astrologers/fetchCallHistory",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getAstrologerCallHistory(params);
      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch call history",
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch call history",
      );
    }
  },
);


// ✅ NEW — Fetch signed recording URL for a voice call
export const fetchCallRecording = createAsyncThunk(
  "astrologers/fetchCallRecording",
  async (callId, { rejectWithValue }) => {
    try {
      const response = await getCallRecording(callId);
      // response.data = { call_id, call_type, duration, expires_in_seconds, recordings: [...] }
      return response.data;
    } catch (error) {
      const msg =
        error.response?.data?.message || "Failed to fetch recording";
      toast.error(msg);
      return rejectWithValue(msg);
    }
  },
);

// Fetch chat history
export const fetchChatHistory = createAsyncThunk(
  "astrologers/fetchChatHistory",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getAstrologerChatHistory(params);
      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch chat history",
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch chat history",
      );
    }
  },
);

export const fetchChatUsersList = createAsyncThunk(
  "chat/fetchUsersList",
  async (params, { rejectWithValue }) => {
    try {
      return await getChatUsersList(params);
    } catch (error) {
      return rejectWithValue("Failed to fetch users");
    }
  }
);

export const fetchChatMessagesList = createAsyncThunk(
  "chat/fetchMessagesList",
  async (params, { rejectWithValue }) => {
    try {
      return await getChatMessagesList(params);
    } catch (error) {
      return rejectWithValue("Failed to fetch messages");
    }
  }
);


// Add a new astrologer
export const addAstrologer = createAsyncThunk(
  "astrologers/addAstrologer",
  async (astrologer, { rejectWithValue }) => {
    try {
      const response = await addLang(astrologer);
      toast.success(response.message || "Data added successfully!");
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
      return rejectWithValue(
        error.response?.data?.message || "An error occurred",
      );
    }
  },
);

// Update a astrologer
export const updateAstrologer = createAsyncThunk(
  "astrologers/updateAstrologer",
  async ({ id, astrologer }, { rejectWithValue }) => {
    try {
      const response = await updateLang(id, astrologer);
      toast.success(response.message || "Data updated successfully!");
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
      return rejectWithValue(
        error.response?.data?.message || "An error occurred",
      );
    }
  },
);

// Update a astrologer status
export const updateAstrologerStatus = createAsyncThunk(
  "astrologers/updateAstrologerStatus",
  async ({ id, statusObj }, { rejectWithValue }) => {
    try {
      const response = await updateLang(id, statusObj);
      toast.success(response.message || "Status updated successfully!");
      return { id, statusObj };
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
      return rejectWithValue(
        error.response?.data?.message || "An error occurred",
      );
    }
  },
);

// Delete a astrologer
export const deleteAstrologer = createAsyncThunk(
  "astrologers/deleteAstrologer",
  async (id, { rejectWithValue }) => {
    try {
      let response = await deleteLang(id);
      toast.success(response.message || "Data deleted successfully!");
      return id;
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
      return rejectWithValue(
        error.response?.data?.message || "An error occurred",
      );
    }
  },
);

// Settle payment for an astrologer
export const settlePayment = createAsyncThunk(
  "astrologers/settlePayment",
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await settleAstrologerPayment(paymentData);
      toast.success(response.message || "Payment settled successfully!");
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "Payment failed");
      return rejectWithValue(error.response?.data?.message || "Payment failed");
    }
  },
);

// Fetch payout history for an astrologer
export const fetchPayoutHistory = createAsyncThunk(
  "astrologers/fetchPayoutHistory",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getAstrologerPayoutHistory(params);
      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch payout history",
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payout history",
      );
    }
  },
);

const astrologerSlice = createSlice({
  name: "astrologers",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
      // ✅ NEW — reset recording state when modal closes
    clearCallRecording: (state) => {
      state.callRecording = null;
      state.callRecordingStatus = "idle";
      state.callRecordingError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Astrologers
      .addCase(fetchAstrologers.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAstrologers.fulfilled, (state, action) => {
        // API response shape:
        // { success, message, data: [...], pagination: { total, page, limit, totalPages } }
        const payload = action.payload;

        state.status = "succeeded";
        state.astrologers = payload.data || [];
        state.total = payload.pagination?.total || 0;
        state.page = payload.pagination?.page || 1;
        state.error = null;
      })
      .addCase(fetchAstrologers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.astrologers = [];
        state.total = 0;
        state.page = 1;
      })

      // Add Astrologer
      .addCase(addAstrologer.fulfilled, (state, action) => {
        // Re-fetch will happen after add via hitFetchApi toggle
        // Optionally unshift if you want optimistic update
        state.astrologers.unshift(action.payload.data);
      })

      // Fetch Call History
      .addCase(fetchCallHistory.pending, (state) => {
        state.callHistoryStatus = "loading";
      })
      .addCase(fetchCallHistory.fulfilled, (state, action) => {
  state.callHistoryStatus = "succeeded";
  state.callHistory = action.payload?.data?.callHistory || [];
  state.callHistoryTotal = action.payload?.data?.total || 0;  // ✅ ADD THIS
})
      .addCase(fetchCallHistory.rejected, (state, action) => {
        state.callHistoryStatus = "failed";
        state.callHistoryError = action.payload;
      })

// ✅ NEW ── Fetch Call Recording ─────────────────────────────────────────
      .addCase(fetchCallRecording.pending, (state) => {
        state.callRecordingStatus = "loading";
        state.callRecordingError = null;
        state.callRecording = null;
      })
      .addCase(fetchCallRecording.fulfilled, (state, action) => {
        // action.payload = { call_id, call_type, duration, expires_in_seconds, recordings: [...] }
        state.callRecordingStatus = "succeeded";
        state.callRecording = action.payload;
      })
      .addCase(fetchCallRecording.rejected, (state, action) => {
        state.callRecordingStatus = "failed";
        state.callRecordingError = action.payload;
        state.callRecording = null;
      })
  
       // Fetch Chat History
  .addCase(fetchChatHistory.pending, (state) => {
    state.chatHistoryStatus = "loading";
    state.chatHistoryError = null;
  })
  .addCase(fetchChatHistory.fulfilled, (state, action) => {
    state.chatHistoryStatus = "succeeded";
    state.chatHistory = action.payload || null;
  })
  .addCase(fetchChatHistory.rejected, (state, action) => {
    state.chatHistoryStatus = "failed";
    state.chatHistoryError = action.payload;
  })

// Fetch Chat Users List
.addCase(fetchChatUsersList.fulfilled, (state, action) => {
  state.chatUsersStatus = "succeeded";

  // 🔥 YOUR API FIX
  state.chatUsersList = action.payload?.data?.chatList || [];
})
// Fetch Chat Messages List
// .addCase(fetchChatMessagesList.fulfilled, (state, action) => {
//   state.chatMessagesStatus = "succeeded";

//   state.chatMessagesList =
//     action.payload?.data?.messages || action.payload?.data || [];
// })

.addCase(fetchChatMessagesList.fulfilled, (state, action) => {
  state.chatMessagesStatus = "succeeded";
  // const messages = action.payload?.data?.messages || action.payload?.data || [];
  // state.chatMessagesList = [...messages].reverse(); 
   state.chatMessagesList = action.payload?.data?.messages || action.payload?.data || [];
})

      // Update Astrologer
      .addCase(updateAstrologer.fulfilled, (state, action) => {
        const updatedAstrologer = action.payload.data;
        if (!Array.isArray(state.astrologers)) return;
        const index = state.astrologers.findIndex(
          (astro) => astro._id === updatedAstrologer._id,
        );
        if (index !== -1) {
          state.astrologers[index] = {
            ...state.astrologers[index],
            ...updatedAstrologer,
          };
        }
      })

      // Update Astrologer Status
      .addCase(updateAstrologerStatus.fulfilled, (state, action) => {
        const { id, statusObj } = action.payload;
        if (!Array.isArray(state.astrologers)) return;
        const index = state.astrologers.findIndex(
          (astrologer) => astrologer._id === id,
        );
        if (index !== -1) {
          state.astrologers[index] = {
            ...state.astrologers[index],
            ...statusObj,
          };
        }
      })

      // Delete Astrologer
      .addCase(deleteAstrologer.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.astrologers = state.astrologers.filter(
          (astro) => astro._id !== deletedId,
        );
        state.total = Math.max(0, state.total - 1);
      })

      // Settle Payment
      .addCase(settlePayment.fulfilled, (state, action) => {
        const updatedAstrologer = action.payload?.data;
        if (!updatedAstrologer) return;
        const index = state.astrologers.findIndex(
          (astro) => astro._id === updatedAstrologer._id,
        );
        if (index !== -1) {
          state.astrologers[index] = {
            ...state.astrologers[index],
            ...updatedAstrologer,
          };
        }
      })

      // Fetch Payout History
      .addCase(fetchPayoutHistory.pending, (state) => {
        state.payoutStatus = "loading";
      })
      .addCase(fetchPayoutHistory.fulfilled, (state, action) => {
        state.payoutStatus = "succeeded";
        state.payoutHistory = action.payload?.data || [];
      })
      .addCase(fetchPayoutHistory.rejected, (state, action) => {
        state.payoutStatus = "failed";
        state.payoutError = action.payload;
      });
  },
});

export const { clearError,clearCallRecording  } = astrologerSlice.actions;
export default astrologerSlice.reducer;
